/**
 * useCDNToken.js · keeps a lesson's CDN links fresh (link refresh, October 2026).
 *
 * Every link /video/:id returns (the video, the answer-key video, both PDFs) carries the same CDN token (?t=), and
 * the token expires (the content API's CDN_TOKEN_TTL_SECONDS). One function keeps it fresh:
 *
 *   ensureCDNTokenFresh(timeNeeded)
 *     checkCDNToken(timeNeeded)  local only: does the token in state have at least timeNeeded seconds left?
 *     refreshCDNToken()          only if not: gets a new token from /video/:id and replaces it in state
 *
 * Two kinds of trigger call it. They only decide WHEN it runs; nothing waits on it.
 *   background     every CDN_TOKEN_CHECK_SECONDS, and on return to the page. On screen (or while a video plays in
 *                  a hidden tab) it asks for what a play press would need RIGHT NOW (2× the rest of the video from
 *                  the current spot, the answer key too if its tab is open), never less than CDN_TOKEN_MIN_SECONDS.
 *                  So a link is only ever renewed when the next play would have needed it anyway, or when it is
 *                  nearly dead: a short pause never causes a refresh, and a link that runs out in the last minutes
 *                  of a video is renewed quietly while the video plays to the end. A hidden page with nothing
 *                  playing asks only for the floor, CDN_TOKEN_MIN_SECONDS: about one request every 1¾ hours, so
 *                  the page never holds a dead link (a player that touches a dead link fails; October 15, 2026).
 *   gestures       play/resume (lesson and answer key), opening the answer-key video, the worksheet and answer-key PDFs.
 *
 * When a request fails, the background check keeps trying at its normal pace for CDN_TOKEN_RETRY_FAST_TRIES tries,
 * then backs off (doubling, up to CDN_TOKEN_RETRY_MAX_SECONDS) — while a video plays; with nothing playing the
 * background retries stand down after a failure (the player's overlay and its tap take over). A return to the page
 * or a gesture always tries at once. A refusal (a 4xx: no longer entitled, or the lesson is gone) stops the background retries until a return
 * or a gesture gets a different answer. A fresh link, or a new lesson, resets all of this.
 *
 * This file's ONLY job is that the page always holds a fresh link in state. What a player does with a fresh link
 * (Shaka swaps the token on its own; Safari's player needs rebuilding) is a separate step and is not in here.
 *
 * Clock check: every check uses the device's clock. A link that has only just arrived can't already be short of time
 * unless that clock is wrong (or CDN_TOKEN_TTL_SECONDS is under CDN_TOKEN_VIDEO_MAX_SECONDS, the most any check can
 * ask for); then the checks stand down for that lesson, which behaves as it did before link refresh, instead of
 * fetching every 30 seconds.
 */
import { useRef, useEffect, useCallback } from 'react';

// ── Tunable times, in seconds. Change them here. ─────────────────────────────────────────────────────────────────
// How often the background check runs while the page is on screen (or a video plays in a hidden tab).
export const CDN_TOKEN_CHECK_SECONDS = 30;
// A link is always renewed below this, whatever the video needs. Also the time a PDF needs (it loads in seconds).
export const CDN_TOKEN_MIN_SECONDS = 15 * 60;
// The most a video play ever needs (the 90-minute cap).
export const CDN_TOKEN_VIDEO_MAX_SECONDS = 90 * 60;
// A play needs this many times the rest of the video (2 = still covered at 0.5× speed).
export const CDN_TOKEN_VIDEO_RATE = 2;
// A request for a fresh link that takes longer than this counts as failed.
export const CDN_TOKEN_FETCH_TIMEOUT_SECONDS = 15;
// After a failed request, the background check retries at its normal pace this many times, then backs off
// (1, 2, 4 min …) up to CDN_TOKEN_RETRY_MAX_SECONDS between tries. A return to the page or a gesture ignores this.
export const CDN_TOKEN_RETRY_FAST_TRIES = 10;
export const CDN_TOKEN_RETRY_MAX_SECONDS = 5 * 60;
// The link lifetime itself is the content API's CDN_TOKEN_TTL_SECONDS.

// Seconds left on the token of a signed link (?t=<prefix>.<exp>.<sig>). null when the link has no readable token:
// then nothing is fetched (the same rule as App.jsx's parseTokenExpiry).
export function cdnTokenSecondsLeft(url) {
  if (typeof url !== 'string') return null;
  const q = url.indexOf('?t=');
  if (q === -1) return null;
  const parts = url.slice(q + 3).split('&')[0].split('.');
  if (parts.length !== 3) return null;
  const exp = Number(parts[1]);
  return Number.isFinite(exp) && exp > 0 ? exp - Date.now() / 1000 : null;
}

// The time a video play needs: CDN_TOKEN_VIDEO_RATE × the rest of the video, at most CDN_TOKEN_VIDEO_MAX_SECONDS.
// Unknown length: the cap. At the very end (Watch Again): the whole video.
export function videoNeedSeconds(duration, position) {
  const d = Number(duration);
  if (!Number.isFinite(d) || d <= 0) return CDN_TOKEN_VIDEO_MAX_SECONDS;
  const p = Math.max(0, Number(position) || 0);
  const rest = d - p < 1 ? d : d - p;
  return Math.min(CDN_TOKEN_VIDEO_MAX_SECONDS, CDN_TOKEN_VIDEO_RATE * rest);
}

// A link of a /video/:id payload: 'lesson', 'ak' (answer-key video), 'homework' or 'answerKey' (the two PDFs).
function linkOf(v, key) {
  if (!v) return null;
  if (key === 'lesson') return (v.video && v.video.url) || null;
  if (key === 'ak') return (v.answerKeyVideo && v.answerKeyVideo.url) || null;
  if (key === 'homework') return (v.homework && v.homework.pdfUrl) || null;
  if (key === 'answerKey') return (v.answerKey && v.answerKey.pdfUrl) || null;
  return null;
}
// One token covers every link in a payload, so any of them tells its time.
const tokenLinkOf = (v) => linkOf(v, 'lesson') || linkOf(v, 'ak') || linkOf(v, 'homework') || linkOf(v, 'answerKey');
// The time a play of the lesson ('lesson') or the answer-key video ('ak') needs, from the page's values l.
const playNeedSeconds = (l, which) => {
  const v = which === 'ak' ? (l.video && l.video.answerKeyVideo) : (l.video && l.video.video);
  return videoNeedSeconds(v && v.durationSeconds, which === 'ak' ? l.akTime : l.currentTime);
};
// What the background check asks for: the most any play press would need right now, never under the floor.
const backgroundNeedSeconds = (l) => {
  const ak = l.video && l.video.answerKeyVideo;
  return Math.max(CDN_TOKEN_MIN_SECONDS, playNeedSeconds(l, 'lesson'), l.akOpen && ak && ak.url ? playNeedSeconds(l, 'ak') : 0);
};
// The clock check, for a payload that has just arrived: its link should have at least CDN_TOKEN_VIDEO_MAX_SECONDS left.
const clockLooksRight = (v) => { const left = cdnTokenSecondsLeft(tokenLinkOf(v)); return left === null || left >= CDN_TOKEN_VIDEO_MAX_SECONDS; };

export default function useCDNToken({ id, api, video, setVideo, ready, isPlaying, currentTime, akPlaying, akTime, akOpen }) {
  // The page's latest values. The functions below read them here, so they stay the same functions for the life
  // of the page.
  const live = useRef(null);
  live.current = { id, api, video, setVideo, ready, isPlaying, currentTime, akPlaying, akTime, akOpen };
  // The clock check, made once per payload, when it arrives (here for the page's own fetch, in refreshCDNToken for ours).
  const arrival = useRef({ video: null, clockOk: true });
  if (arrival.current.video !== video) arrival.current = { video, clockOk: clockLooksRight(video) };

  const inflight = useRef(null);                              // { id, promise }: the request running (one per lesson)
  // Failures of the last requests, in a row; when the background check may try again; whether the server refused.
  const retry = useRef({ failures: 0, notBefore: 0, refused: false });

  // ── The three functions ───────────────────────────────────────────────────────────────────────────────────────
  const checkCDNToken = useCallback((timeNeeded) => {
    if (!arrival.current.clockOk) return true;
    const left = cdnTokenSecondsLeft(tokenLinkOf(live.current.video));
    return left === null || left >= timeNeeded;
  }, []);

  // Resolves with what happened:
  //   'fresh'    a fresh link replaced the one in state
  //   'offline'  no answer: offline, timed out (CDN_TOKEN_FETCH_TIMEOUT_SECONDS), the server is down (5xx) or busy (408, 429)
  //   'refused'  the server answered without a link (no longer entitled, or the lesson is gone)
  //   'ignored'  the reply was for another lesson
  // Only the retry schedule (below) reads the result; nothing waits on it, the next trigger simply checks again.
  const refreshCDNToken = useCallback(() => {
    const reqId = live.current.id;
    if (inflight.current && inflight.current.id === reqId) return inflight.current.promise;
    const ctl = typeof AbortController === 'function' ? new AbortController() : null;
    let timer = null;
    // The limit is a race, so it holds even if the request itself never settles (e.g. a stuck sign-in refresh).
    const timeout = new Promise((resolve) => { timer = window.setTimeout(() => { if (ctl) ctl.abort(); resolve(null); }, CDN_TOKEN_FETCH_TIMEOUT_SECONDS * 1000); });
    // The request starts inside a promise, so even a synchronous throw from api.fetch ends up as 'offline'.
    const request = Promise.resolve().then(() => live.current.api.fetch('/video/' + reqId, ctl ? { signal: ctl.signal } : {}));
    const promise = Promise.race([request, timeout])
      .then((res) => {
        if (!res || res.status >= 500 || res.status === 408 || res.status === 429) return 'offline';
        if (!res.ok) return 'refused';
        return res.json().then((body) => {
          const data = body && body.data;
          const l = live.current;
          if (l.id !== reqId || !l.ready) return 'ignored';
          if (!data || !tokenLinkOf(data)) return 'refused';
          l.video = data;   // later checks in this tick read the new links before the page re-renders
          arrival.current = { video: data, clockOk: clockLooksRight(data) };
          l.setVideo(data);
          return 'fresh';
        });
      })
      .catch(() => 'offline')
      .then((result) => {
        window.clearTimeout(timer);
        if (inflight.current && inflight.current.promise === promise) inflight.current = null;
        if (live.current.id !== reqId) return result;   // a reply for a previous lesson: not this lesson's schedule
        const r = retry.current;
        if (result === 'fresh') retry.current = { failures: 0, notBefore: 0, refused: false };
        else if (result === 'refused') r.refused = true;
        else if (result === 'offline') {
          r.refused = false;   // the last answer decides: a refusal is only known while it is the latest reply
          r.failures += 1;
          const extra = r.failures - CDN_TOKEN_RETRY_FAST_TRIES;   // tries past the fast ones: 1 → 1 min, 2 → 2, 3 → 4 …
          const wait = extra <= 0 ? 0 : Math.min(CDN_TOKEN_RETRY_MAX_SECONDS, CDN_TOKEN_CHECK_SECONDS * 2 ** extra);
          r.notBefore = Date.now() + wait * 1000;
        }
        return result;
      });
    inflight.current = { id: reqId, promise };
    return promise;
  }, []);

  // Resolves 'ok' when the link already has the time, or with refreshCDNToken's result. Nothing waits on it.
  const ensureCDNTokenFresh = useCallback((timeNeeded) => {
    if (!live.current.ready || checkCDNToken(timeNeeded)) return Promise.resolve('ok');
    return refreshCDNToken();
  }, [checkCDNToken, refreshCDNToken]);

  // ── Triggers 1 and 2: background, every CDN_TOKEN_CHECK_SECONDS and on return to the page ────────────────────
  useEffect(() => {
    if (!ready) return undefined;
    const check = () => {
      const l = live.current;
      // Hidden with nothing playing: only the floor, so the link never runs out while the page lives.
      const idle = document.visibilityState === 'hidden' && !l.isPlaying && !l.akPlaying;
      ensureCDNTokenFresh(idle ? CDN_TOKEN_MIN_SECONDS : backgroundNeedSeconds(l));
    };
    // The timer honours the retry schedule; a return to the page (the events) always checks at once.
    // After a failed request with nothing playing, the timer stands down (October 30, 2026): the player's overlay
    // is up, and its tap fetches the token and the link itself; a return to the page or a gesture tries again.
    // While a video plays the retries go on, so a link is renewed before it runs out.
    const tick = () => {
      const r = retry.current; const l = live.current;
      if (r.refused || Date.now() < r.notBefore) return;
      if (r.failures > 0 && !l.isPlaying && !l.akPlaying) return;
      check();
    };
    const timer = window.setInterval(tick, CDN_TOKEN_CHECK_SECONDS * 1000);
    document.addEventListener('visibilitychange', check);
    window.addEventListener('pageshow', check);
    window.addEventListener('focus', check);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('pageshow', check);
      window.removeEventListener('focus', check);
    };
  }, [ready, ensureCDNTokenFresh]);

  // A new lesson starts with a clean retry slate.
  useEffect(() => { retry.current = { failures: 0, notBefore: 0, refused: false }; }, [id]);

  // ── Triggers 3 and 4: play / resume. Each video starting (its page flag turning on) is a check for the time
  // that play needs. The players and their play setters are untouched: this watches the page's own flags.
  useEffect(() => { if (isPlaying) ensureCDNTokenFresh(playNeedSeconds(live.current, 'lesson')); }, [isPlaying, ensureCDNTokenFresh]);
  useEffect(() => { if (akPlaying) ensureCDNTokenFresh(playNeedSeconds(live.current, 'ak')); }, [akPlaying, ensureCDNTokenFresh]);

  // ── Trigger 5: the answer-key video's tab or section opening ─────────────────────────────────────────────────
  useEffect(() => {
    if (!akOpen) return;
    const ak = live.current.video && live.current.video.answerKeyVideo;
    if (ak && ak.url) ensureCDNTokenFresh(videoNeedSeconds(ak.durationSeconds, live.current.akTime));
  }, [akOpen, ensureCDNTokenFresh]);

  // ── Triggers 6 and 7: the worksheet PDF ('homework') and the answer-key PDF ('answerKey') ─────────────────────
  // Opens the link in state inside the click (as the pages always did) and runs the check alongside it.
  const openPdf = useCallback((key) => {
    const url = linkOf(live.current.video, key);
    if (!url) return;
    ensureCDNTokenFresh(CDN_TOKEN_MIN_SECONDS);
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [ensureCDNTokenFresh]);

  return { ensureCDNTokenFresh, checkCDNToken, refreshCDNToken, openPdf };
}
